import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  ButtonDirective,
  FormControlDirective,
  FormFeedbackComponent,
  InputGroupComponent,
  InputGroupTextDirective
} from '@coreui/angular-pro';
import { IconDirective, IconModule } from '@coreui/icons-angular';
import { I18nService, TranslatePipe } from 'i18n';
import { Logger, UnassistedInputDirective } from 'shared';

const MCAPTCHA_ORIGIN = 'https://mc.fac3less.app';

/**
 * Public withdrawal-request page at /withdrawal (no :lang prefix, linked from
 * the landing page's always-visible WITHDRAWAL button). Submits first/last
 * name + email to POST /api/withdrawal; the backend registers the request,
 * sends the confirmation email with a #FAC-XXXXXX reference and follows up
 * after matching against the customer database.
 */
@Component({
  selector: 'app-withdrawal-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonDirective,
    FormControlDirective,
    FormFeedbackComponent,
    InputGroupComponent,
    InputGroupTextDirective,
    IconDirective,
    IconModule,
    UnassistedInputDirective,
    TranslatePipe
  ],
  templateUrl: './withdrawal-page.component.html',
  styleUrls: ['./withdrawal-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WithdrawalPageComponent {
  private fb         = inject(FormBuilder);
  private http       = inject(HttpClient);
  private i18n       = inject(I18nService);
  private destroyRef = inject(DestroyRef);
  private logger     = new Logger('WithdrawalPage');

  form!: FormGroup;

  captchaToken   = signal<string | null>(null);
  captchaError   = signal<boolean>(false);
  mcaptchaConfig = signal<{ widgetUrl: string; sitekey: string } | null>(null);

  loading   = signal<boolean>(false);
  error     = signal<string | null>(null);
  /** Set after a successful submit — switches the card to the confirmation view. */
  reference = signal<string | null>(null);

  firstNameCtrl = computed(() => this.form.get('first_name')!);
  lastNameCtrl  = computed(() => this.form.get('last_name')!);
  emailCtrl     = computed(() => this.form.get('email')!);

  formStatus!: ReturnType<typeof toSignal<string>>;
  canSubmit!: ReturnType<typeof computed<boolean>>;

  constructor() {
    this.form = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(2)]],
      last_name:  ['', [Validators.required, Validators.minLength(2)]],
      email:      ['', [Validators.required, Validators.email]]
    });

    this.formStatus = toSignal(this.form.statusChanges, { initialValue: this.form.status });
    this.canSubmit  = computed(() =>
      this.formStatus() === 'VALID' && !!this.captchaToken() && !this.loading()
    );

    effect(() => this.loadMcaptchaConfig());
    effect(() => { if (this.mcaptchaConfig()) this.loadMcaptchaWidget(); });

    window.addEventListener('message', this.handleMcaptchaMessage.bind(this));
  }

  // ---- mCaptcha (same flow as the funnel landing page) -------------------

  private loadMcaptchaConfig(): void {
    this.http.get<{ widgetUrl: string; sitekey: string }>('/auth/mcaptcha_config')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: cfg => this.mcaptchaConfig.set(cfg),
        error: err => {
          this.logger.error('failed to load mcaptcha config', err);
          this.error.set('Could not load captcha. Please refresh.');
        }
      });
  }

  private loadMcaptchaWidget(): void {
    const cfg = this.mcaptchaConfig();
    if (!cfg) return;

    const container = document.getElementById('mcaptcha-widget');
    if (!container) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${cfg.widgetUrl}?sitekey=${cfg.sitekey}`;
    iframe.style.border = 'none';
    iframe.style.width = '100%';
    iframe.style.height = '95px';
    iframe.style.overflow = 'hidden';
    iframe.title = 'mCaptcha verification';
    iframe.setAttribute('scrolling', 'no');

    container.innerHTML = '';
    container.appendChild(iframe);
  }

  private handleMcaptchaMessage(event: MessageEvent): void {
    if (event.origin !== MCAPTCHA_ORIGIN) return;

    const data = event.data;
    let token: string | null = null;
    if (typeof data === 'string')   token = data;
    else if (data?.token)           token = data.token;
    else if (data?.m_captcha_token) token = data.m_captcha_token;

    if (token) {
      this.captchaToken.set(token);
      this.captchaError.set(false);
    }
  }

  // ---- Submit -------------------------------------------------------------

  onSubmit(): void {
    if (this.form.invalid || !this.captchaToken()) {
      if (!this.captchaToken()) this.captchaError.set(true);
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.http.post<{ status: string; reference: string }>('/withdrawal', {
      ...this.form.value,
      language:      this.i18n.getLanguage() || 'en',
      captcha_token: this.captchaToken()
    }).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.loading.set(false);
          this.reference.set(res.reference);
        },
        error: err => {
          this.loading.set(false);
          this.captchaToken.set(null); // captcha is single-use — reset so user can retry
          this.error.set(err?.error?.error || 'Something went wrong. Please try again.');
        }
      });
  }
}
